import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Instagram, MessageCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fadeUp = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] },
};

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast.success("Mesajınız başarıyla gönderildi! En kısa sürede size ulaşacağız.");
      setFormData({
        name: "",
        email: "",
        phone: "",
        subject: "",
        message: "",
      });
    } catch (error) {
      toast.error("Bir hata oluştu. Lütfen tekrar deneyin.");
      console.error("Failed to submit:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pt-24" data-testid="contact-page">
      {/* Hero Section */}
      <section className="py-16 bg-[#0D3B2E]" data-testid="contact-hero">
        <div className="section-container">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <span className="caption-text text-[#C5A059] mb-4 block">
              Bize Ulaşın
            </span>
            <h1 className="heading-xl text-[#F9F7F2]">İletişim</h1>
            <p className="body-text text-[#F9F7F2]/70 mt-4 max-w-xl">
              Sorularınız, önerileriniz veya işbirliği teklifleriniz için bizimle
              iletişime geçin.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-16 bg-[#F9F7F2] border-b border-[#E5E0D8]">
        <div className="section-container">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                icon: Phone,
                title: "Telefon",
                info: "+90 549 351 71 21",
                link: "tel:+905493517121",
              },
              {
                icon: Mail,
                title: "E-posta",
                info: "info@sumbulaga.com",
                link: "mailto:info@sumbulaga.com",
              },
              {
                icon: MessageCircle,
                title: "WhatsApp",
                info: "WhatsApp ile yazın",
                link: "https://wa.me/905493517121",
              },
              {
                icon: Instagram,
                title: "Instagram",
                info: "@sumbulaga",
                link: "https://instagram.com/sumbulaga",
              },
            ].map((item, index) => (
              <motion.a
                key={index}
                href={item.link}
                target={item.link.startsWith("http") ? "_blank" : undefined}
                rel={item.link.startsWith("http") ? "noopener noreferrer" : undefined}
                {...fadeUp}
                transition={{ delay: index * 0.1 }}
                className="flex items-center gap-4 p-6 bg-white border border-[#E5E0D8] hover:border-[#C5A059] transition-colors"
                data-testid={`contact-${item.title.toLowerCase()}`}
              >
                <item.icon className="w-8 h-8 text-[#C5A059]" strokeWidth={1.5} />
                <div>
                  <p className="caption-text text-[#5D4037]/70 text-[10px] mb-1">
                    {item.title}
                  </p>
                  <p className="body-text text-[#0D3B2E]">{item.info}</p>
                </div>
              </motion.a>
            ))}
          </div>
        </div>
      </section>

      {/* Form & Map Section */}
      <section className="section-spacing bg-[#F9F7F2]" data-testid="contact-form-section">
        <div className="section-container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Form */}
            <motion.div {...fadeUp}>
              <h2 className="heading-lg text-[#0D3B2E] mb-6">Mesaj Gönderin</h2>
              <div className="decorative-line mb-8" />

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Adınız *"
                      className="input-premium w-full"
                      required
                      data-testid="contact-name"
                    />
                  </div>
                  <div>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="E-posta *"
                      className="input-premium w-full"
                      required
                      data-testid="contact-email"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="Telefon"
                      className="input-premium w-full"
                      data-testid="contact-phone"
                    />
                  </div>
                  <div>
                    <input
                      type="text"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      placeholder="Konu *"
                      className="input-premium w-full"
                      required
                      data-testid="contact-subject"
                    />
                  </div>
                </div>
                <div>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Mesajınız *"
                    rows={6}
                    className="input-premium w-full resize-none"
                    required
                    data-testid="contact-message"
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="btn-gold w-full disabled:opacity-50"
                  data-testid="contact-submit"
                >
                  {loading ? <span className="spinner mx-auto" /> : "Gönder"}
                </button>
              </form>
            </motion.div>

            {/* Map */}
            <motion.div {...fadeUp}>
              <h2 className="heading-lg text-[#0D3B2E] mb-6">Konum</h2>
              <div className="decorative-line mb-8" />

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <MapPin className="w-6 h-6 text-[#C5A059] flex-shrink-0 mt-1" strokeWidth={1.5} />
                  <div>
                    <p className="body-text text-[#0D3B2E] mb-1">SümbülAğa Türk Kahvesi</p>
                    <p className="body-text text-[#5D4037] text-sm">
                      Karaköy, İstanbul
                      <br />
                      Türkiye
                    </p>
                  </div>
                </div>

                {/* Map Placeholder */}
                <div
                  className="map-container relative overflow-hidden"
                  data-testid="map-placeholder"
                >
                  <div
                    className="absolute inset-0 bg-cover bg-center"
                    style={{
                      backgroundImage: `url('https://api.mapbox.com/styles/v1/mapbox/light-v11/static/28.9784,41.0082,13,0/600x400@2x?access_token=pk.placeholder')`,
                      filter: "grayscale(100%) sepia(20%) contrast(90%)",
                    }}
                  />
                  <div className="absolute inset-0 bg-[#0D3B2E]/10" />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="bg-white p-4 shadow-floating">
                      <MapPin className="w-8 h-8 text-[#C5A059]" strokeWidth={1.5} />
                    </div>
                  </div>
                  {/* Fallback pattern */}
                  <div
                    className="absolute inset-0 opacity-10"
                    style={{
                      backgroundImage: `repeating-linear-gradient(
                        45deg,
                        #0D3B2E 0px,
                        #0D3B2E 1px,
                        transparent 1px,
                        transparent 10px
                      )`,
                    }}
                  />
                </div>

                <p className="body-text text-[#5D4037] text-sm">
                  * Gerçek harita entegrasyonu için Google Maps API gereklidir.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ CTA */}
      <section className="py-20 bg-[#0D3B2E]">
        <div className="section-container text-center">
          <h2 className="heading-lg text-[#F9F7F2] mb-4">
            Sık Sorulan Sorular
          </h2>
          <p className="body-text text-[#F9F7F2]/70 mb-8 max-w-xl mx-auto">
            Merak ettiğiniz konular için blog sayfamızı ziyaret edebilirsiniz.
          </p>
          <a href="/blog" className="btn-gold inline-block">
            Blog'a Git
          </a>
        </div>
      </section>
    </div>
  );
}
